import "@tento-ui/components/entry-list/entry-list";
import "@tento-ui/components/sidebar/sidebar-section-nav";

import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { html } from "@tento-ui/component";
import { EntryList } from "@tento-ui/components/entry-list/entry-list";
import type { ListItem } from "@tento-ui/components/entry-list/list-item";
import { FlowContainer } from "@tento-ui/components/flow-container";
import { SidebarSectionNav } from "@tento-ui/components/sidebar/sidebar-section-nav";
import { TimezoneRegion } from "@tento-chrono";
import { LonaModalWebComponent } from "@tento-ui/ui/modal-component";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";
import { CalendarsClient, type DateFormat, EventItemPresentation, type TimeFormat } from "@tento-lona";
import { type EventId, EventItemTimed, type Option } from "@tento-lona/sheets";
import type { SheetRow } from "@tento-lona";
import { ExistingEventDetails } from "./existing-event-details.ts";
import { EventWriteRefusal } from "./event-write-refusal.ts";
import type { EventActiveState } from "../../event/event-active-state.ts";

@component({
  name: "lona-event-list-modal",
})
export class EventListModal extends LonaModalWebComponent {
  #$flowContainer: FlowContainer<"list" | "details"> =
    this.hydrate("flow");
  #$header: SidebarSectionNav = this.hydrate("header");
  #$headerTitle = this.$("header-title");
  #$headerCaption = this.$("header-caption");
  #$eventList: EntryList = EntryList.make();

  #tz: Option<TimezoneRegion> = null;
  #calendars: Option<CalendarsClient> = null;
  #row: Option<SheetRow.Handle | SheetRow.Joined.Handle> = null;
  #listDescription: string = "";
  #timeFormat: TimeFormat = "12h";
  #dateFormat: DateFormat = "month-day";
  #onShowFlightInfo: Option<() => void> = null;
  #eventActiveState: Option<EventActiveState> = null;
  #activeDetailsEventId: Option<EventId> = null;
  /** The details panel's writes; `null` where nothing writes (a preview):
   *  the list then opens no details. */
  #events: Option<ExistingEventDetails.Writer> = null;
  /** The opening list's access; a read-only list opens read-only details. */
  #list: ExistingEventDetails.Access = "read-only";

  constructor() {
    super();

    this.#$header.setCallbacks({
      onBackButtonClick: () => {
        if (this.#$flowContainer.numPages <= 1) return;
        this.#clearActiveDetails();
        this.#$flowContainer.popPage();
      },
    });

    this.#$flowContainer.onPageInfoChange = (page) => {
      if (page === "list") {
        this.#$header.toggleBackButton(false, true);
        this.#$headerTitle.textContent = "More Events";
        this.#$headerCaption.textContent = this.#listDescription;
        this.#$headerCaption.style.display = "block";
      } else if (page === "details") {
        this.#$header.toggleBackButton(true, true);
        this.#$headerTitle.textContent = "Event Details";
        this.#$headerCaption.textContent = "";
        this.#$headerCaption.style.display = "none";
      }
    };
  }

  bind(
    eventItems: EventItemTimed[],
    description: string,
    tz: TimezoneRegion,
    calendars: CalendarsClient,
    row: SheetRow.Handle | SheetRow.Joined.Handle,
    timeFormat: TimeFormat,
    dateFormat: DateFormat,
    onShowFlightInfo: () => void,
    eventActiveState: EventActiveState,
    /** The client's event categories and drafts (`client.events`): titles and colours. */
    presentation: EventItemPresentation.Scope,
    events: Option<ExistingEventDetails.Writer>,
    /** The access of the calendar list the events were listed from. */
    list: ExistingEventDetails.Access,
  ): this {
    this.#listDescription = description;
    this.#$headerCaption.textContent = description;
    this.#tz = tz;
    this.#calendars = calendars;
    this.#row = row;
    this.#timeFormat = timeFormat;
    this.#dateFormat = dateFormat;
    this.#onShowFlightInfo = onShowFlightInfo;
    this.#eventActiveState = eventActiveState;
    this.#events = events;
    this.#list = list;
    this.#activeDetailsEventId = null;

    const items: ListItem[] = eventItems.map((event) => {
      const rawColor = EventItemPresentation.backgroundColor(event, presentation);
      const accentColor = rawColor !== "var(--gray)" ? rawColor : "var(--bg)";
      return {
        title: EventItemPresentation.title(event, presentation),
        accentColor,
        at: event.time.time.start,
        timezone: tz,
        onClick: () => {
          this.#pushEventDetails(event);
        },
      };
    });

    this.#$eventList.bind({
      items,
      display: { timeScale: "day", use24Hour: timeFormat === "24h", dateFormat },
    });

    this.#$flowContainer.setSizing({
      mode: "grow",
      scroll: "container",
      maxHeight: null,
    });
    this.#$flowContainer.reset();
    this.#$flowContainer.pushPage(this.#$eventList, "list", false);
    this.#$flowContainer.render(null, true);

    return this;
  }

  #pushEventDetails(event: EventItemTimed): void {
    if (
      !this.#tz || !this.#calendars || !this.#row || !this.#onShowFlightInfo ||
      this.#events == null
    ) {
      return;
    }

    const $details = ExistingEventDetails.make(
      event,
      this.#tz,
      this.#calendars,
      this.#row,
      this.#timeFormat,
      this.#dateFormat,
      this.#onShowFlightInfo,
      this.#events,
      this.#list,
      EventWriteRefusal.toast,
    );

    this.#clearActiveDetails();
    this.#$flowContainer.pushPage($details, "details");
    this.#eventActiveState?.activate("details", event.id, null);
    this.#activeDetailsEventId = event.id;
  }

  showAsModal(): void {
    this.reveal();
  }

  onModalDismiss(): void {
    this.#clearActiveDetails();
  }

  #clearActiveDetails(): void {
    if (this.#activeDetailsEventId == null) return;
    this.#eventActiveState?.clear("details", this.#activeDetailsEventId);
    this.#activeDetailsEventId = null;
  }

  static $styles: Style.Compat[] = [
    LonaModalWebComponent.$style,
    LonaModalHeader.$style,
    css`
      :host {
        --std-modal-max-width: 400px;
        --std-modal-min-height: auto;
        --std-modal-max-height: calc(100vh - 400px);
        background: var(--bg);
        border-radius: 12px;
      }

      #root {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      #header {
        margin-bottom: 0px;
        --back-btn-margin-left: 8px;
        --back-btn-margin-block: 12px;
        --nav-content-revealed-offset: -12px;
      }

      hr {
        margin: 0;
        border: none;
        border-top: 1px solid var(--divider);
      }

      #flow {
        flex: 1;
        overflow: hidden;
        --flow-bg: transparent;
        --flow-shadow: none;
      }

      .lona-modal-header {
        align-self: center;
      }

      #header-title {
        --margin-bottom: 0px;
      }

      #header-caption {
        --margin-top: 6px;
      }

      std-entry-list {
        padding: 4px;
        width: calc(100%);
        min-height: 200px;
      }

      lona-existing-event-details-editable {
        min-height: 200px;
        height: 100%;
      }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-sidebar-section-nav id="header">
        <std-col slot="title" class="lona-modal-header">
          <h1 id="header-title">More Events</h1>
          <p id="header-caption"></p>
        </std-col>
      </std-sidebar-section-nav>
      <hr />
      <std-flow-container id="flow"></std-flow-container>
    </std-col>
  `;
}
